import { styled } from '@linaria/react'
import React, { type FC } from 'react'
import { FormattedMessage } from 'react-intl'
import { colors } from '../styles/colors'
import TonIcon from '../svgr/TonIcon'
import ArrowRightUpIcon from '../svgr/ArrowUpRight'
import { Typography } from '../ui/Typography'
import { formatTon } from '../utils/formatTon'
import { useTonWallet } from '@tonconnect/ui-react'
import { useTelegram } from '../hooks/useTelegram'
import { useClaimRewards } from '../hooks/useBonusData'
import { WithdrawalConfirmDialog } from '../dialogs/WithdrawalConfirmDialog'
import { WithdrawalInProgressDialog } from '../dialogs/WithdrawalInProgressDialog'
import { InsufficientAmountDialog } from '../dialogs/InsufficientAmountDialog'
import { Dialog } from '../dialogs/Dialog'
import { toNano } from '@ton/core'
import type { BonusStatsDto } from '../types/generated'
import {
  trackBonusWithdrawalSuccess,
  trackBonusWithdrawalFailed,
} from '../utils/amplitude'

export const MIN_WITHDRAW_AMOUNT_TON = 3
export const MIN_WITHDRAW_AMOUNT_NANO = toNano(MIN_WITHDRAW_AMOUNT_TON)

interface BonusWithdrawalCardProps {
  stats: BonusStatsDto | undefined
}

export const BonusWithdrawalCard: FC<BonusWithdrawalCardProps> = ({
  stats,
}) => {
  const wallet = useTonWallet()
  const walletAddress = wallet?.account.address
  const { tg, isTelegramVersionSupported } = useTelegram()
  const claimMutation = useClaimRewards()
  const [showWithdrawalDialog, setShowWithdrawalDialog] = React.useState(false)
  const [showWithdrawalInProgressDialog, setShowWithdrawalInProgressDialog] =
    React.useState(false)
  const [showInsufficientAmountDialog, setShowInsufficientAmountDialog] =
    React.useState(false)

  const handleWithdraw = async () => {
    // Use haptic feedback only if supported
    if (isTelegramVersionSupported(6.1)) {
      tg.HapticFeedback.impactOccurred('medium')
    }

    if (!walletAddress) {
      if (isTelegramVersionSupported(6.1)) {
        tg.showPopup({
          title: '❌ Error',
          message: 'Please connect your wallet to withdraw rewards',
          buttons: [{ type: 'ok' }],
        })
      } else {
        // Fallback for older versions
        alert('❌ Error: Please connect your wallet to withdraw rewards')
      }
      return
    }

    // Check if withdrawal is in flight
    if (stats && parseFloat(formatTon(stats.inFlight, 2)) > 0) {
      if (isTelegramVersionSupported(6.1)) {
        tg.HapticFeedback.notificationOccurred('warning')
      }
      setShowWithdrawalInProgressDialog(true)
      return
    }

    // Check minimum amount
    if (!stats || BigInt(stats.canWithdraw) < MIN_WITHDRAW_AMOUNT_NANO) {
      if (isTelegramVersionSupported(6.1)) {
        tg.HapticFeedback.notificationOccurred('warning')
      }
      setShowInsufficientAmountDialog(true)
      return
    }

    // Show confirmation dialog
    const amount = formatTon(stats.canWithdraw, 2)

    if (isTelegramVersionSupported(6.1)) {
      tg.showConfirm(
        `Withdraw ${amount} TON to your wallet?`,
        async confirmed => {
          if (!confirmed) {
            return
          }

          try {
            tg.HapticFeedback.impactOccurred('heavy')
            await claimMutation.mutateAsync({ walletAddress })
            trackBonusWithdrawalSuccess(amount)
            tg.HapticFeedback.notificationOccurred('success')

            tg.showPopup({
              title: '🎉 Success!',
              message: `Withdrawal request for ${amount} TON submitted successfully! Your TON will arrive shortly.`,
              buttons: [{ type: 'ok' }],
            })
          } catch (err) {
            const errorMessage =
              err instanceof Error ? err.message : 'Failed to submit withdrawal'

            trackBonusWithdrawalFailed(errorMessage)
            tg.HapticFeedback.notificationOccurred('error')

            tg.showPopup({
              title: '❌ Withdrawal Failed',
              message: errorMessage,
              buttons: [{ type: 'ok' }],
            })
          }
        },
      )
    } else {
      // Use custom dialog for non-Telegram environments
      setShowWithdrawalDialog(true)
    }
  }

  const canWithdrawAmountNano = stats ? BigInt(stats.canWithdraw) : BigInt(0)
  const inFlightAmount = stats ? parseFloat(formatTon(stats.inFlight, 2)) : 0

  // Format display text for current rewards
  const getCurrentRewardsDisplay = () => {
    const canWithdrawFormatted = formatTon(stats?.canWithdraw || '0', 2)

    if (inFlightAmount > 0) {
      const inFlightFormatted = formatTon(stats?.inFlight || '0', 2)
      return `${canWithdrawFormatted} (+ ${inFlightFormatted} withdrawal in flight)`
    }

    return canWithdrawFormatted
  }

  return (
    <>
      <StatCard
        onClick={handleWithdraw}
        as="button"
        data-testid="withdrawal-card"
      >
        <StatValue fontSize={13} fontWeight={700}>
          {getCurrentRewardsDisplay()} <TonIcon />
          {canWithdrawAmountNano >= MIN_WITHDRAW_AMOUNT_NANO && (
            <StyledWithdrawIcon />
          )}
        </StatValue>

        <Typography fontSize={10} color={colors.textSecondary}>
          <FormattedMessage
            id="bonus.currentRewards"
            defaultMessage="Текущие награды"
          />
        </Typography>
      </StatCard>

      {wallet && showWithdrawalDialog && stats && (
        <Dialog onClose={() => setShowWithdrawalDialog(false)}>
          <WithdrawalConfirmDialog
            amount={formatTon(stats.canWithdraw, 2)}
            wallet={wallet}
            onConfirm={async () => {
              await claimMutation.mutateAsync({
                walletAddress: wallet.account.address,
              })
            }}
            isLoading={claimMutation.isPending}
          />
        </Dialog>
      )}

      {showWithdrawalInProgressDialog && (
        <Dialog onClose={() => setShowWithdrawalInProgressDialog(false)}>
          <WithdrawalInProgressDialog />
        </Dialog>
      )}

      {showInsufficientAmountDialog && (
        <Dialog onClose={() => setShowInsufficientAmountDialog(false)}>
          <InsufficientAmountDialog />
        </Dialog>
      )}
    </>
  )
}

const StatCard = styled.div`
  display: grid;
  gap: 2px;
  background: ${colors.surfaceSecondary};
  border-radius: 16px;
  padding: 9px 16px;
  align-content: center;
`

const StatValue = styled(Typography)`
  position: relative;
  display: grid;
  grid-auto-flow: column;
  justify-content: flex-start;
  align-items: center;
  gap: 4px;
  flex-gap: 4px;
`

const StyledWithdrawIcon = styled(ArrowRightUpIcon)`
  width: 20px;
  height: 20px;
`
